import { ApiRequest, PagedList } from 'framework/api';
import { RulesConfiguration } from 'controls/queryBuilder/ruleDefinition';
import { GridDataItem } from 'controls/grid/gridDataItem';
import { HealthReason } from 'framework/entities/healthReason';
import { GridSelectionState } from 'controls/grid/gridSelection';

export type GetReasonsArgs = {
	accountId?: string;
	includeSubaccounts?: boolean;
	tags?: string[];
	showUntagged?: boolean;
	assetIds?: string[];
	assetGroupIds?: string[];
	applicationIds?: string[];
	monitorIds?: string[];
	mode?: 'ALL' | 'LAST';
	severity?: string;
};

export function getReasons(args?: GetReasonsArgs) {
	return new ApiRequest<PagedList<GridDataItem<HealthReason>>>({
		url: 'health/reasons/summaryList',
		accountBased: true,
		accountId: args?.accountId,
		subaccountsFilter: true,
		includeSubaccounts: args?.includeSubaccounts,
		method: 'POST',
		payload: {
			tags: args?.tags,
			showUntagged: args?.showUntagged,
			assetIds: args?.assetIds,
			assetGroupIds: args?.assetGroupIds,
			applicationIds: args?.applicationIds,
			monitorIds: args?.monitorIds,
			mode: args?.mode,
			severity: args?.severity,
		},
	});
}

export type GetSelectedReasonsArgs = {
	accountId?: string;
	includeSubaccounts?: boolean;
	selection?: GridSelectionState;
};

export function getSelectedReasons(args?: GetSelectedReasonsArgs) {
	return new ApiRequest<HealthReason[]>({
		url: 'health/reasons/summaryList/selectedItems',
		accountBased: true,
		accountId: args?.accountId,
		subaccountsFilter: true,
		includeSubaccounts: args?.includeSubaccounts,
		method: 'POST',
		payload: {
			selection: args?.selection,
		},
	});
}

export function getReasonsFiltersConfiguration(args?: GetReasonsArgs) {
	return new ApiRequest<RulesConfiguration>({
		url: 'health/reasons/summaryList/ruleConfiguration',
		accountBased: true,
		accountId: args?.accountId,
		subaccountsFilter: true,
		method: 'GET',
	});
}

export type ReasonsActionArgs = GetReasonsArgs & {
	selection?: GridSelectionState;
};

export type AssignReasonsArgs = ReasonsActionArgs & {
	assignToUserId?: string;
	assignToTeamId?: string;
};

export type ResetReasonsHealthIndexArgs = ReasonsActionArgs & {
	resetReason?: string;
};

export type SetReasonsInformationArgs = ReasonsActionArgs & {
	information?: string;
};

function reasonsSummaryListAction(action: string, args?: ReasonsActionArgs, extraPayload?: object) {
	return new ApiRequest({
		url: `health/reasons/summaryList/${action}`,
		accountBased: true,
		accountId: args?.accountId,
		subaccountsFilter: true,
		includeSubaccounts: args?.includeSubaccounts,
		method: 'POST',
		payload: {
			tags: args?.tags,
			showUntagged: args?.showUntagged,
			assetIds: args?.assetIds,
			assetGroupIds: args?.assetGroupIds,
			applicationIds: args?.applicationIds,
			monitorIds: args?.monitorIds,
			mode: args?.mode,
			severity: args?.severity,
			selection: args?.selection,
			...extraPayload,
		},
	});
}

export function deleteReasons(args?: ReasonsActionArgs) {
	return reasonsSummaryListAction('delete', args);
}

export function acknowledgeReasons(args?: ReasonsActionArgs) {
	return reasonsSummaryListAction('acknowledgeAssetHealthEvents', args);
}

export function unacknowledgeReasons(args?: ReasonsActionArgs) {
	return reasonsSummaryListAction('unacknowledgeAssetHealthEvents', args);
}

export function assignReasons(args?: AssignReasonsArgs) {
	return reasonsSummaryListAction('assignAssetHealthEvents', args, {
		assignToUserId: args?.assignToUserId,
		assignToTeamId: args?.assignToTeamId,
	});
}

export function unassignReasons(args?: ReasonsActionArgs) {
	return reasonsSummaryListAction('unassignAssetHealthEvents', args);
}

export function resetReasonsHealthIndex(args?: ResetReasonsHealthIndexArgs) {
	return reasonsSummaryListAction('resetHealthIndexes', args, { resetReason: args?.resetReason });
}

export function setReasonsInformation(args?: SetReasonsInformationArgs) {
	return reasonsSummaryListAction('information', args, { information: args?.information });
}
